"use client";

import { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildJournalIndex,
  isoDotted,
  type JournalIndex,
  type JournalRecord,
  searchJournals,
} from "@/lib/tool-kit/identifiers/journal-abbreviations";
import { loadJournalList } from "@/lib/tool-kit/identifiers/lookup";

/** English strings. A translated content file overrides these through `ui`. */
export const JOURNAL_ABBREVIATION_FINDER_STRINGS = {
  queryLabel: "Journal title, abbreviation or ISSN",
  queryPlaceholder: "New England Journal of Medicine, J Am Chem Soc, 0028-4793",
  queryHint:
    "Capitals, accents and punctuation are ignored, so J. Am. Chem. Soc. finds J Am Chem Soc.",
  typeMore: "Type at least two characters to search the NLM journal list.",
  loading: "Loading the NLM journal list…",
  loadFailed:
    "The journal list could not be loaded. Check your connection and try again.",
  retry: "Try again",
  results: "Matches",
  matchCount: "one:{count} journal matches|other:{count} journals match",
  showingFirst:
    "Showing the closest {shown} — add a word to narrow the search.",
  noMatch:
    "No journal in the NLM list matches “{query}”. The list is mainly biomedical and life sciences; for other journals, apply the ISO 4 rules below.",
  medline: "MEDLINE / NLM abbreviation",
  iso: "ISO abbreviation (as NLM records it)",
  dotted: "With ISO 4 full stops",
  dottedNote:
    "Full stops added after each shortened word — check against the journal's own style.",
  issnPrint: "ISSN (print)",
  issnOnline: "ISSN (online)",
  noAbbreviation: "NLM gives no abbreviation for this title.",
  none: "—",
  copy: "Copy",
  copied: "Copied!",
  source:
    "Source: the PubMed journal list (J_Medline.txt) from the U.S. National Library of Medicine, updated {date}.",
} as const;

type Strings = typeof JOURNAL_ABBREVIATION_FINDER_STRINGS;
type LoadState = "idle" | "loading" | "ready" | "error";

const RESULT_LIMIT = 25;

function AbbreviationLine({
  label,
  value,
  strings,
  note,
}: {
  label: string;
  value: string;
  strings: Strings;
  note?: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className="font-mono text-foreground">{value}</p>
        {note ? <p className={HINT}>{note}</p> : null}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={value}
      />
    </div>
  );
}

function JournalCard({
  record,
  strings,
}: {
  record: JournalRecord;
  strings: Strings;
}) {
  const dotted = record.medAbbr ? isoDotted(record.medAbbr, record.title) : "";
  return (
    <li className={`${CARD} space-y-3`}>
      <p className="font-medium text-foreground">{record.title}</p>
      {record.medAbbr ? (
        <div className="space-y-3">
          <AbbreviationLine
            label={strings.medline}
            strings={strings}
            value={record.medAbbr}
          />
          <AbbreviationLine
            label={strings.iso}
            strings={strings}
            value={record.isoAbbr}
          />
          {dotted !== record.medAbbr ? (
            <AbbreviationLine
              label={strings.dotted}
              note={strings.dottedNote}
              strings={strings}
              value={dotted}
            />
          ) : null}
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">
          {strings.noAbbreviation}
        </p>
      )}
      <dl className="grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-muted-foreground text-xs">{strings.issnPrint}</dt>
          <dd className="font-mono text-foreground">
            {record.issnPrint || strings.none}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">
            {strings.issnOnline}
          </dt>
          <dd className="font-mono text-foreground">
            {record.issnOnline || strings.none}
          </dd>
        </div>
      </dl>
    </li>
  );
}

function formatUpdated(updated: string, locale: string): string {
  const date = new Date(`${updated}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) {
    return updated;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "long",
    timeZone: "UTC",
  }).format(date);
}

export default function JournalAbbreviationFinderWidget() {
  const strings = useToolUi(JOURNAL_ABBREVIATION_FINDER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [query, setQuery] = useState("");
  const [state, setState] = useState<LoadState>("idle");
  const [index, setIndex] = useState<JournalIndex | null>(null);
  const [updated, setUpdated] = useState("");
  const deferredQuery = useDeferredValue(query);

  const wantsData = query.trim().length >= 2;
  useEffect(() => {
    if (!wantsData || state !== "idle") {
      return;
    }
    setState("loading");
    loadJournalList()
      .then((list) => {
        setIndex(buildJournalIndex(list.records));
        setUpdated(list.updated);
        setState("ready");
      })
      .catch(() => setState("error"));
  }, [wantsData, state]);

  const found = useMemo(
    () => (index ? searchJournals(index, deferredQuery, RESULT_LIMIT) : null),
    [index, deferredQuery]
  );

  return (
    <section className="space-y-6">
      <TextField
        hint={strings.queryHint}
        id="jaf-query"
        label={strings.queryLabel}
        onChange={setQuery}
        placeholder={strings.queryPlaceholder}
        type="search"
        value={query}
      />
      <div aria-live="polite" className="space-y-4">
        {wantsData ? null : <Notice>{strings.typeMore}</Notice>}
        {wantsData && state === "loading" ? (
          <Notice>{strings.loading}</Notice>
        ) : null}
        {wantsData && state === "error" ? (
          <div className="space-y-3">
            <ErrorNotice>{strings.loadFailed}</ErrorNotice>
            <button
              className={BUTTON_SECONDARY}
              onClick={() => setState("idle")}
              type="button"
            >
              {strings.retry}
            </button>
          </div>
        ) : null}
        {wantsData && found && found.total === 0 ? (
          <Notice>{t(strings.noMatch, { query: query.trim() })}</Notice>
        ) : null}
        {wantsData && found && found.total > 0 ? (
          <>
            <div>
              <h2 className={WIDGET_H2}>{strings.results}</h2>
              <p className="text-muted-foreground text-sm">
                {tPlural(strings.matchCount, locale, found.total, {
                  count: fmt(found.total),
                })}
                {found.total > RESULT_LIMIT
                  ? ` ${t(strings.showingFirst, { shown: fmt(RESULT_LIMIT) })}`
                  : null}
              </p>
            </div>
            <ul className="space-y-3">
              {found.matches.map(({ record }) => (
                <JournalCard
                  key={`${record.title}|${record.medAbbr}`}
                  record={record}
                  strings={strings}
                />
              ))}
            </ul>
          </>
        ) : null}
        {state === "ready" && updated ? (
          <p className={HINT}>
            {t(strings.source, { date: formatUpdated(updated, locale) })}
          </p>
        ) : null}
      </div>
    </section>
  );
}
